import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import type { VendorRow, VendorsView } from '../../lib/financeTypes';
import { Banner, EmptyState, Field, LoadingPage, Tag } from '../../components/ui';
import { Metric, pctLabel, usd } from './shared';

export function Vendors() {
  const [report, setReport] = useState<string | undefined>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [recurringOnly, setRecurringOnly] = useState(false);
  const state = useAsync(() => api.finVendors(report), [report]);
  const data = state.data;

  if (!data) return state.loading ? <LoadingPage label="Loading vendor spending…" /> : null;
  if (!data.report) {
    return <EmptyState title="No approved statement yet." hint="Vendor spending is built from the general ledger of approved statements." />;
  }

  if (openId) {
    const vendor = data.vendors.find((v) => v.id === openId);
    return <VendorDetailView vendor={vendor} data={data} onBack={() => setOpenId(null)} onChanged={() => void state.reload()} />;
  }

  const rows = data.vendors.filter(
    (v) =>
      (!q || v.name.toLowerCase().includes(q.toLowerCase())) &&
      (!category || v.category === category) &&
      (!recurringOnly || v.recurring),
  );
  const maxCat = Math.max(...data.categories.map((c) => c.amount), 1);
  const recurringTotal = data.vendors.filter((v) => v.recurring).reduce((t, v) => t + v.ytd, 0);
  const unconfirmed = data.vendors.filter((v) => !v.categoryConfirmed).length;

  return (
    <div>
      <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <strong>{data.report.periodLabel} · year to date</strong>
        {data.reports.length > 1 && (
          <select aria-label="Statement period" value={report ?? data.report.id} onChange={(e) => setReport(e.target.value)} style={{ maxWidth: 220 }}>
            {data.reports.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="stat-grid">
          <Metric label="Total spending" value={usd(data.totalSpend)} hint="Both funds, from the general ledger" />
          <Metric label="Vendors" value={data.vendors.length} />
          <Metric label="Recurring vendors" value={usd(recurringTotal)} hint="Year-to-date spend with recurring vendors" />
          <Metric label="Categories to confirm" value={unconfirmed} hint="Suggested, not yet confirmed" />
        </div>
      </div>

      <div className="grid two">
        <div className="card">
          <h3>Spending by category</h3>
          {data.categories.map((c) => (
            <div key={c.name} style={{ marginTop: 8 }}>
              <div className="row between">
                <span>{c.name}<span className="mut"> · {c.vendors} vendor{c.vendors === 1 ? '' : 's'}</span></span>
                <span className="mut nowrap">{usd(c.amount)} · {pctLabel(c.pct, 0)}</span>
              </div>
              <div className="bar-track"><div className="bar" style={{ width: `${(Math.max(c.amount, 0) / maxCat) * 100}%` }} /></div>
            </div>
          ))}
        </div>

        <div className="card">
          <h3>Vendor review opportunities</h3>
          <p className="mut">{data.note}</p>
          {data.review.length === 0 ? (
            <p className="mut">Nothing stands out this month.</p>
          ) : (
            data.review.slice(0, 12).map((item, index) => (
              <div className="note" key={`${item.type}-${index}`}>
                <div className="note-head">
                  <span className="who">{item.title}</span>
                  <Tag tone="info">{REVIEW_LABEL[item.type] ?? 'Look closer'}</Tag>
                </div>
                <p className="note-body">{item.detail}</p>
                {item.vendorId && <button type="button" className="btn tiny ghost" onClick={() => setOpenId(item.vendorId ?? null)}>Open vendor</button>}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="card tw">
        <div className="card-head"><h3>Vendors</h3></div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input placeholder="Search vendors" aria-label="Search vendors" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 220 }} />
          <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="">All categories</option>
            {data.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={recurringOnly} onChange={(e) => setRecurringOnly(e.target.checked)} /> Recurring only
          </label>
        </div>
        <table>
          <thead>
            <tr><th>Vendor</th><th>Category</th><th className="num">This month</th><th className="num">Year to date</th><th className="num">Share</th><th className="num">Owed (A/P)</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id}>
                <td>
                  {v.name}
                  {v.recurring && <Tag>Recurring</Tag>}
                  {v.spike && <Tag tone="warn">High this month</Tag>}
                </td>
                <td>{v.category}{!v.categoryConfirmed && <span className="mut"> (suggested)</span>}</td>
                <td className="num">{usd(v.month)}</td>
                <td className="num">{usd(v.ytd)}</td>
                <td className="num">{pctLabel(v.pctOfExpenses, 1)}</td>
                <td className="num">{usd(v.outstanding)}</td>
                <td className="num"><button type="button" className="btn tiny" onClick={() => setOpenId(v.id)}>Details</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="mut">No vendors match.</p>}
        <p className="mut">{data.budgetNote}</p>
      </div>
    </div>
  );
}

const REVIEW_LABEL: Record<string, string> = {
  'budget-variance': 'Over budget',
  unbudgeted: 'No budget',
  'contract-increase': 'Recurring change',
  'contract-exceeded': 'Above contract',
  spike: 'Unusual month',
  'new-vendor': 'New this year',
  concentration: 'Concentration',
  'possible-duplicate': 'Possible duplicate',
};

function VendorDetailView({
  vendor,
  data,
  onBack,
  onChanged,
}: {
  vendor: VendorRow | undefined;
  data: VendorsView;
  onBack: () => void;
  onChanged: () => void;
}) {
  const { notify, fail } = useToast();
  const id = vendor?.id ?? '';
  const detail = useAsync(() => api.finVendor(id, data.report?.id), [id]);
  const [mergeInto, setMergeInto] = useState('');
  const [contract, setContract] = useState(vendor?.contractMonthly?.toString() ?? '');
  const [saving, setSaving] = useState(false);

  if (!vendor) return <button type="button" className="btn s" onClick={onBack}>Back</button>;

  async function update(input: Parameters<typeof api.finVendorUpdate>[1], message: string) {
    setSaving(true);
    try {
      await api.finVendorUpdate(vendor!.id, input);
      notify(message, 'success');
      onChanged();
    } catch (error) {
      fail(error, 'Could not update the vendor.');
    } finally {
      setSaving(false);
    }
  }

  const d = detail.data;
  const monthNames = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  const maxMonth = Math.max(...vendor.monthly, 1);

  return (
    <div>
      <button type="button" className="btn s ghost" onClick={onBack} style={{ marginBottom: 12 }}>← All vendors</button>
      <div className="card">
        <div className="card-head"><h3>{vendor.name}</h3>{vendor.recurring && <Tag>Recurring</Tag>}</div>
        <div className="stat-grid">
          <Metric label="Year to date" value={usd(vendor.ytd)} />
          <Metric label="This month" value={usd(vendor.month)} hint={vendor.monthOverMonth === null ? undefined : `${vendor.monthOverMonth >= 0 ? '+' : '−'}${usd(Math.abs(vendor.monthOverMonth))} vs last month`} />
          <Metric label="Share of spending" value={pctLabel(vendor.pctOfExpenses)} />
          <Metric label="Invoices" value={vendor.invoices} />
          <Metric label="Currently owed" value={usd(vendor.outstanding)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 70, marginTop: 14 }} aria-label="Spending by month">
          {vendor.monthly.map((m, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }} title={`${usd(m)}`}>
              <div style={{ height: Math.max(2, (Math.max(m, 0) / maxMonth) * 54), background: 'var(--mid)', borderRadius: 4 }} />
              <span className="mut" style={{ fontSize: 11 }}>{monthNames[i]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Category and contract</h3>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="Category" hint={vendor.categoryConfirmed ? 'Confirmed by the board' : 'Suggested from the vendor name. Confirm or change it.'}>
            <select
              value={vendor.categoryId ?? ''}
              disabled={saving}
              onChange={(e) => e.target.value && void update({ categoryId: e.target.value }, 'Category saved.')}
            >
              <option value="">{vendor.category} (suggested)</option>
              {data.categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Recurring?" hint={vendor.recurringSource === 'detected' ? 'Detected from the monthly pattern' : 'Set by the board'}>
            <select
              value={vendor.recurring ? 'yes' : 'no'}
              disabled={saving}
              onChange={(e) => void update({ recurring: e.target.value === 'yes' }, 'Saved.')}
            >
              <option value="yes">Recurring</option>
              <option value="no">Not recurring</option>
            </select>
          </Field>
          <Field label="Contract amount per month" hint="Optional. Used to flag months above contract.">
            <input value={contract} inputMode="decimal" onChange={(e) => setContract(e.target.value)} />
          </Field>
          <button
            type="button"
            className="btn s"
            disabled={saving}
            onClick={() => void update({ contractMonthly: contract.trim() === '' ? null : Number(contract) }, 'Contract amount saved.')}
          >
            Save amount
          </button>
        </div>
      </div>

      <div className="card tw">
        <h3>Transactions in {data.report?.periodLabel}</h3>
        <p className="mut">Every line links back to its page in the original statement.</p>
        {!d ? <LoadingPage label="Loading transactions…" /> : (
          <>
            {d.aliases.length > 0 && <p className="mut">Also appears as: {d.aliases.join(', ')}</p>}
            <table>
              <thead><tr><th>Date</th><th>Description</th><th>Account</th><th className="num">Amount</th><th className="num">Page</th></tr></thead>
              <tbody>
                {d.transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="nowrap">{t.postedOn ?? ''}</td>
                    <td>{t.description}<div className="mut">{t.document ?? ''}</div></td>
                    <td className="mut">{t.account}</td>
                    <td className="num">{usd(t.amount, true)}</td>
                    <td className="num mut">{t.page}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.transactions.length === 0 && <p className="mut">No ledger lines for this vendor.</p>}
          </>
        )}
      </div>

      <div className="card">
        <h3>Same vendor under another name?</h3>
        <p className="mut">Merging keeps the other spelling as an alias; no figures are lost.</p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select aria-label="Merge into" value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} style={{ maxWidth: 280 }}>
            <option value="">Merge “{vendor.name}” into…</option>
            {data.vendors.filter((v) => v.id !== vendor.id).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button
            type="button"
            className="btn s danger"
            disabled={!mergeInto || saving}
            onClick={async () => {
              if (!window.confirm(`Merge ${vendor.name} into the selected vendor?`)) return;
              setSaving(true);
              try {
                await api.finVendorMerge(vendor.id, mergeInto);
                notify('Vendors merged.', 'success');
                onChanged();
                onBack();
              } catch (error) {
                fail(error, 'Could not merge those vendors.');
              } finally {
                setSaving(false);
              }
            }}
          >
            Merge
          </button>
        </div>
      </div>
      <Banner kind="info">{data.note}</Banner>
    </div>
  );
}
